import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import CompactVolumeSlider from './CompactVolumeSlider';
import { subscribeToVolume, setGlobalVolume } from '../utils/globalVolume';
const YouTubeTile = ({ videoId, username, showUsername, isProducer, isHost, onPlay, onPause, onStop, onVideoEnded, socket, onReady, onClick, onCommand, startTime, isPaused, pausedAt, isGloballyMuted = false, isBroadcast = false, seekTo, isFeatured = false }) => {
    // CRITICAL: All useRef declarations MUST be first to avoid temporal dead zone
    const playerRef = useRef(null);
    const containerRef = useRef(null);
    const initializationRef = useRef(false);
    const lastAutoPlayedVideoRef = useRef(null);
    const endedEventSentRef = useRef(false);
    const currentLoadedVideoRef = useRef(null); // Track which video is currently loaded
    /** serverNow - clientNow at receipt: converts server epoch to local epoch. */
    const clockOffsetRef = useRef(0);
    const [playerInitialized, setPlayerInitialized] = useState(false);
    const [hasEnded, setHasEnded] = useState(false);
    const [needsUserInteraction, setNeedsUserInteraction] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playerReady, setPlayerReady] = useState(false);
    const [isDestroying, setIsDestroying] = useState(false);
    const [volume, setVolume] = useState(0); // Synced with global volume
    const [isHovered, setIsHovered] = useState(false);
    // Subscribe to global volume changes
    useEffect(() => {
        const unsubscribe = subscribeToVolume((newVolume) => {
            setVolume(newVolume);
        });
        return unsubscribe;
    }, []);
    console.log(`ðŸŽ¬ YouTubeTile render for ${videoId} - isHost: ${isHost}, isProducer: ${isProducer}, socket: ${!!socket}`);
    // DEBUG: Log when isHost prop changes
    useEffect(() => {
        console.log(`ðŸ‘‘ YouTubeTile ${videoId} - isHost changed to: ${isHost}`);
    }, [isHost, videoId]);
    // Handle host-only click control - FIXED: Check if player methods exist
    const handleTileClick = useCallback(() => {
        console.log(`ðŸŽ¬ YouTubeTile click - isHost: ${isHost}, isProducer: ${isProducer}, playerReady: ${playerReady}, socket: ${!!socket}, isBroadcast: ${isBroadcast}`);
        // CRITICAL: Broadcast videos cannot be controlled by clicking
        if (isBroadcast) {
            console.log('ðŸš« Broadcast video click ignored - no manual controls for broadcast');
            return;
        }
        // CRITICAL: Only hosts can control YouTube videos by clicking
        if (!isHost) {
            console.log('ðŸš« Non-host click ignored - host-only control');
            return;
        }
        if (!socket) {
            console.log('âŒ No socket available for YouTube control');
            return;
        }
        if (!playerRef.current || !playerReady || isDestroying) {
            console.log('âš ï¸ Player not ready for control or is destroying');
            return;
        }
        try {
            // CRITICAL FIX: Verify player methods exist before calling
            if (typeof playerRef.current.getPlayerState !== 'function') {
                console.log('âš ï¸ Player not fully initialized, getPlayerState method not available yet');
                return;
            }
            const playerState = playerRef.current.getPlayerState();
            console.log('ðŸŽ® Current player state:', playerState, '(1=playing, 2=paused)');
            // YouTube player states: 1 = playing, 2 = paused
            if (playerState === 1) {
                // Currently playing - pause it
                console.log('â¸ï¸ Host clicking to PAUSE for all users');
                socket.emit('youtube-control', { action: 'pause' });
            }
            else {
                // Currently paused or other state - play it
                console.log('â–¶ï¸ Host clicking to PLAY for all users');
                socket.emit('youtube-control', { action: 'play' });
            }
        }
        catch (error) {
            console.error('âŒ Error in host click control:', error);
        }
    }, [isHost, isProducer, socket, playerReady, isDestroying, isBroadcast]);
    // Handle video ended - CRITICAL: Auto-advance to next video
    const handleVideoEnded = useCallback(() => {
        console.log('ðŸ Video ended naturally - triggering auto-advance');
        if (socket && !isDestroying) {
            console.log('ðŸ“¤ Emitting auto-advance command to server');
            socket.emit('chat-command', {
                command: 'auto-advance',
                args: []
            });
        }
        if (onVideoEnded) {
            onVideoEnded();
        }
    }, [socket, isDestroying, onVideoEnded]);
    // YouTube player configuration - CRITICAL: Disable ALL controls for non-hosts
    const getPlayerVars = () => {
        const vars = {
            autoplay: 1,
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
            iv_load_policy: 3,
            cc_load_policy: 0,
            playsinline: 1,
            enablejsapi: 1,
            origin: window.location.origin
        };
        // STATE PRESERVATION: Include start time if seekTo is provided
        if (seekTo !== undefined && seekTo !== null) {
            vars.start = Math.floor(seekTo);
            console.log(`ðŸŽ¬ Including start=${vars.start} in player config`);
        }
        return vars;
    };
    // Initialize YouTube player - ENHANCED with method verification
    const initializePlayer = useCallback(() => {
        if (!videoId || initializationRef.current || isDestroying) {
            console.log('ðŸš« Skipping player init - no videoId, already initialized, or destroying');
            return;
        }
        if (!window.YT || !window.YT.Player) {
            console.log('âš ï¸ YouTube API not ready yet, will retry...');
            return;
        }
        const container = document.getElementById('youtube-player-singleton');
        if (!container) {
            console.log('âš ï¸ Container element not ready yet, will retry...');
            return;
        }
        console.log(`ðŸŽ¬ YouTubeTile initializing player for ${videoId} (host: ${isHost})`);
        initializationRef.current = true;
        try {
            if (playerRef.current) {
                console.log('ðŸ§¹ Destroying existing player before creating new one');
                playerRef.current.destroy();
                playerRef.current = null;
            }
            // CRITICAL: Reset ended flag when creating new player
            endedEventSentRef.current = false;
            console.log('ðŸ—ï¸ Creating new YouTube player...');
            currentLoadedVideoRef.current = videoId; // Mark this video as loaded
            playerRef.current = new window.YT.Player('youtube-player-singleton', {
                videoId: videoId,
                width: '310',
                height: '180',
                playerVars: getPlayerVars(),
                events: {
                    onReady: (event) => {
                        console.log('âœ… YouTube player ready');
                        // CRITICAL FIX: Verify methods are available before marking ready
                        if (playerRef.current && typeof playerRef.current.getPlayerState === 'function') {
                            setPlayerReady(true);
                            setPlayerInitialized(true);
                            console.log('âœ… Player methods verified, marking as ready');
                            // CRITICAL: Mute broadcast videos to enable autoplay (browser policy)
                            if (isBroadcast) {
                                try {
                                    console.log('ðŸ”‡ Muting broadcast video to enable autoplay');
                                    playerRef.current.mute();
                                }
                                catch (error) {
                                    console.warn('âš ï¸ Could not mute video:', error);
                                }
                            }
                            // CRITICAL: Explicitly start playback to ensure autoplay works
                            // This is essential for scheduled videos and browser autoplay policies
                            try {
                                console.log('â–¶ï¸ Explicitly calling playVideo() to ensure autoplay');
                                playerRef.current.playVideo();
                            }
                            catch (error) {
                                console.warn('âš ï¸ Could not auto-play video:', error);
                            }
                        }
                        else {
                            console.log('âš ï¸ Player object exists but methods not ready, waiting...');
                            setTimeout(() => {
                                if (playerRef.current && typeof playerRef.current.getPlayerState === 'function') {
                                    setPlayerReady(true);
                                    setPlayerInitialized(true);
                                    console.log('âœ… Player methods verified after delay, marking as ready');
                                    // CRITICAL: Mute broadcast videos to enable autoplay (browser policy)
                                    if (isBroadcast) {
                                        try {
                                            console.log('ðŸ”‡ Muting broadcast video to enable autoplay (delayed)');
                                            playerRef.current.mute();
                                        }
                                        catch (error) {
                                            console.warn('âš ï¸ Could not mute video:', error);
                                        }
                                    }
                                    // CRITICAL: Explicitly start playback after delay
                                    try {
                                        console.log('â–¶ï¸ Explicitly calling playVideo() after delay');
                                        playerRef.current.playVideo();
                                    }
                                    catch (error) {
                                        console.warn('âš ï¸ Could not auto-play video after delay:', error);
                                    }
                                }
                            }, 500);
                        }
                        if (!isHost) {
                            console.log('ðŸ”’ Disabling player interactions for non-host');
                            try {
                                const iframe = document.querySelector('#youtube-player-singleton iframe');
                                if (iframe) {
                                    iframe.style.pointerEvents = 'none';
                                    console.log('ðŸ”’ Iframe pointer events disabled for non-host');
                                }
                            }
                            catch (error) {
                                console.warn('âš ï¸ Could not disable iframe interactions:', error);
                            }
                        }
                        if (onReady)
                            onReady();
                    },
                    onStateChange: (event) => {
                        const state = event.data;
                        console.log('ðŸŽµ YouTube state change:', state, '(1=playing, 2=paused, 0=ended)');
                        setIsPlaying(state === 1);
                        if (state === 1) {
                            setPlayerReady(true);
                            console.log('âœ… Player ready state set via playing state');
                            endedEventSentRef.current = false; // Reset ended flag when video starts playing
                            // Broadcast videos STAY muted - user must manually unmute via volume slider
                            // NO auto-unmute logic here!
                        }
                        if (state === 0) {
                            console.log('ðŸ Video ended naturally');
                            console.log('ðŸ” End event check:', {
                                hasSocket: !!socket,
                                isDestroying,
                                alreadySent: endedEventSentRef.current,
                                isBroadcast
                            });
                            setHasEnded(true);
                            // CRITICAL: Different behavior for broadcast vs queue videos
                            if (socket && !isDestroying && !endedEventSentRef.current) {
                                endedEventSentRef.current = true;
                                if (isBroadcast) {
                                    // Broadcast video - emit broadcast-video-ended to advance broadcast rotation
                                    console.log('ðŸ“¤ Broadcast video ended - emitting broadcast-video-ended');
                                    socket.emit('broadcast-video-ended');
                                }
                                else {
                                    // Queue video - emit auto-advance command
                                    console.log('ðŸ“¤ Queue video ended - emitting auto-advance command to server');
                                    socket.emit('chat-command', {
                                        command: 'auto-advance',
                                        args: []
                                    });
                                }
                            }
                            else {
                                console.log('âš ï¸ NOT sending end event:', {
                                    reason: !socket ? 'no socket' : isDestroying ? 'destroying' : 'already sent'
                                });
                            }
                            if (onVideoEnded) {
                                onVideoEnded();
                            }
                        }
                    },
                    onError: (event) => {
                        console.error('âŒ YouTube player error:', event.data);
                        setNeedsUserInteraction(true);
                        initializationRef.current = false;
                    }
                }
            });
            console.log('ðŸŽ¯ YouTube player constructor completed');
        }
        catch (error) {
            console.error('âŒ Failed to initialize YouTube player:', error);
            initializationRef.current = false;
        }
    }, [videoId, isHost, onReady, socket, isDestroying, onVideoEnded, isBroadcast, seekTo]);
    // The room's clock, not this machine's. `youtube-video-updated` carries
    // serverNow on every emit; we only want the offset from it, so this listener
    // deliberately does nothing else and cannot conflict with the existing
    // prop-driven flow.
    useEffect(() => {
        if (!socket)
            return;
        const onUpdate = (u) => {
            if (typeof u?.serverNow === 'number') {
                clockOffsetRef.current = u.serverNow - Date.now();
            }
        };
        socket.on('youtube-video-updated', onUpdate);
        return () => socket.off('youtube-video-updated', onUpdate);
    }, [socket]);
    /**
     * YouTube's iframe API only accepts discrete playback rates
     * (getAvailablePlaybackRates() is typically [0.25 … 2]), so RemoteTile's
     * gentle 4% nudge is not available here — 1.25 is the smallest catch-up and it
     * is plainly audible. Seeking is the lesser evil, so the threshold is set a
     * little wider than RemoteTile's to avoid thrashing on ordinary jitter.
     */
    const SYNC_DRIFT_SEC = 2.5;
    const RECONCILE_MS = 2000;
    /** Where the room says we should be, right now, in media seconds. */
    const targetPosition = useCallback(() => {
        if (!startTime)
            return null;
        const serverNow = Date.now() + clockOffsetRef.current;
        const at = isPaused && pausedAt ? pausedAt : serverNow;
        return Math.max(0, (at - startTime) / 1000);
    }, [startTime, isPaused, pausedAt]);
    useEffect(() => {
        if (!playerReady || isDestroying)
            return;
        const reconcile = () => {
            const p = playerRef.current;
            const target = targetPosition();
            if (!p || target === null)
                return;
            if (typeof p.getCurrentTime !== 'function' || typeof p.seekTo !== 'function')
                return;
            let cur;
            try {
                cur = p.getCurrentTime();
            }
            catch {
                return; // player mid-teardown
            }
            if (!Number.isFinite(cur))
                return;
            if (isPaused) {
                if (Math.abs(cur - target) > 0.5)
                    p.seekTo(target, true);
                try {
                    if (p.getPlayerState?.() === 1)
                        p.pauseVideo();
                }
                catch {
                    /* ignore */
                }
                return;
            }
            if (Math.abs(cur - target) > SYNC_DRIFT_SEC) {
                console.log(`⏩ YouTube drift ${(cur - target).toFixed(1)}s — seeking to ${target.toFixed(1)}s`);
                p.seekTo(target, true);
            }
        };
        reconcile();
        const t = setInterval(reconcile, RECONCILE_MS);
        return () => clearInterval(t);
    }, [playerReady, isDestroying, targetPosition, isPaused]);
    // Listen for YouTube control broadcasts
    useEffect(() => {
        if (!socket || isDestroying)
            return;
        const handleYouTubeControlBroadcast = (data) => {
            console.log('ðŸ“º YouTubeTile received control broadcast:', data);
            try {
                switch (data.action) {
                    case 'play':
                        console.log('â–¶ï¸ Playing video via broadcast');
                        if (playerRef.current && !isDestroying) {
                            playerRef.current.playVideo();
                            setIsPlaying(true);
                        }
                        break;
                    case 'pause':
                        console.log('â¸ï¸ Pausing video via broadcast');
                        if (playerRef.current && !isDestroying) {
                            playerRef.current.pauseVideo();
                            setIsPlaying(false);
                        }
                        break;
                    case 'stop':
                        console.log('â¹ï¸ Stopping video via broadcast - DESTROYING TILE');
                        setIsDestroying(true);
                        if (playerRef.current) {
                            try {
                                playerRef.current.stopVideo();
                                playerRef.current.destroy();
                                playerRef.current = null;
                            }
                            catch (error) {
                                console.warn('âš ï¸ Error stopping YouTube player:', error);
                            }
                        }
                        setIsPlaying(false);
                        if (onStop) {
                            onStop();
                        }
                        break;
                    case 'skip':
                        console.log('â­ï¸ Skipping video via broadcast - DESTROYING TILE');
                        setIsDestroying(true);
                        if (playerRef.current) {
                            try {
                                playerRef.current.stopVideo();
                                playerRef.current.destroy();
                                playerRef.current = null;
                            }
                            catch (error) {
                                console.warn('âš ï¸ Error skipping YouTube player:', error);
                            }
                        }
                        setIsPlaying(false);
                        if (onStop) {
                            onStop();
                        }
                        break;
                    default:
                        console.log('â“ Unknown YouTube control action:', data.action);
                }
            }
            catch (error) {
                console.error('âŒ Error executing YouTube control:', error);
            }
        };
        socket.on('youtube-control', handleYouTubeControlBroadcast);
        return () => socket.off('youtube-control', handleYouTubeControlBroadcast);
    }, [socket, isDestroying, onStop]);
    // Handle global mute for YouTube player
    // BROADCAST VIDEOS: Ignore this effect - they are controlled ONLY by volume slider
    // NON-BROADCAST VIDEOS: Apply global mute state
    useEffect(() => {
        if (!playerRef.current || !playerReady || isDestroying)
            return;
        if (isBroadcast)
            return; // Broadcast videos controlled by volume slider only
        try {
            if (typeof playerRef.current.mute === 'function' && typeof playerRef.current.unMute === 'function') {
                if (isGloballyMuted) {
                    playerRef.current.mute();
                    console.log('ðŸ”‡ YouTube player muted');
                }
                else {
                    playerRef.current.unMute();
                    console.log('ðŸ”Š YouTube player unmuted');
                }
            }
        }
        catch (error) {
            console.error('âŒ Error controlling YouTube mute state:', error);
        }
    }, [isGloballyMuted, playerReady, isDestroying, isBroadcast]);
    // Handle volume changes and mute state
    useEffect(() => {
        if (!playerRef.current || !playerReady || isDestroying)
            return;
        try {
            if (typeof playerRef.current.setVolume === 'function') {
                playerRef.current.setVolume(volume);
                // Explicitly mute/unmute based on volume
                if (volume === 0 && typeof playerRef.current.mute === 'function') {
                    playerRef.current.mute();
                }
                else if (volume > 0 && typeof playerRef.current.unMute === 'function') {
                    playerRef.current.unMute();
                }
                console.log(`ðŸ”Š YouTube volume set to ${volume}% (${volume === 0 ? 'muted' : 'unmuted'})`);
            }
        }
        catch (error) {
            console.error('âŒ Error setting YouTube volume:', error);
        }
    }, [volume, playerReady, isDestroying]);
    // Auto-play when video advances (isProducer indicates this is the active video)
    useEffect(() => {
        if (!isProducer || !playerRef.current || !playerReady || isDestroying)
            return;
        // CRITICAL FIX: Only auto-play if this is a NEW video (videoId changed)
        if (videoId === lastAutoPlayedVideoRef.current) {
            console.log('â­ï¸ Skipping auto-play - already played this video:', videoId);
            return;
        }
        // Small delay to ensure player is fully ready
        const autoPlayTimer = setTimeout(() => {
            if (playerRef.current && playerReady && !isDestroying) {
                console.log('ðŸŽ¬ Auto-playing NEW video after auto-advance:', videoId);
                try {
                    playerRef.current.playVideo();
                    lastAutoPlayedVideoRef.current = videoId; // Mark as played
                    setIsPlaying(true);
                }
                catch (error) {
                    console.error('âŒ Auto-play failed:', error);
                }
            }
        }, 500);
        return () => clearTimeout(autoPlayTimer);
    }, [isProducer, playerReady, isDestroying, videoId]);
    // Initialize player ONCE and update video when videoId changes
    useEffect(() => {
        if (isDestroying)
            return;
        const attemptInitialization = () => {
            if (!window.YT || !window.YT.Player) {
                console.log('ðŸ”„ YouTube API not ready, retrying in 100ms...');
                setTimeout(attemptInitialization, 100);
                return;
            }
            const container = document.getElementById('youtube-player-singleton');
            if (!container) {
                console.log('ðŸ”„ Container not ready, retrying in 100ms...');
                setTimeout(attemptInitialization, 100);
                return;
            }
            // If player exists, check if we need to load a new video
            if (playerRef.current) {
                // Skip if this video is already loaded (prevents double-loading)
                if (currentLoadedVideoRef.current === videoId) {
                    console.log(`âœ… Video ${videoId} is already loaded, skipping duplicate load`);
                    return;
                }
                console.log('ðŸŽ¬ Player exists, loading new video:', videoId);
                try {
                    // STATE PRESERVATION: Use seekTo if provided (including 0)
                    if (seekTo !== undefined && seekTo !== null) {
                        console.log(`â© Loading video with seekTo: ${seekTo}s`);
                        playerRef.current.loadVideoById({ videoId: videoId, startSeconds: seekTo });
                    }
                    else {
                        playerRef.current.loadVideoById(videoId);
                    }
                    currentLoadedVideoRef.current = videoId; // Mark new video as loaded
                    endedEventSentRef.current = false; // Reset for new video
                    return;
                }
                catch (error) {
                    console.log('âš ï¸ Failed to load new video, will recreate player');
                    playerRef.current.destroy();
                    playerRef.current = null;
                    currentLoadedVideoRef.current = null;
                }
            }
            // Create new player only if it doesn't exist
            if (!playerRef.current) {
                initializePlayer();
            }
        };
        setTimeout(attemptInitialization, 50);
    }, [videoId, isDestroying, initializePlayer]);
    // Cleanup on unmount or destruction
    useEffect(() => {
        return () => {
            if (playerRef.current && !isDestroying) {
                try {
                    console.log('ðŸ§¹ Cleaning up YouTube player on unmount');
                    playerRef.current.destroy();
                    playerRef.current = null;
                }
                catch (error) {
                    console.warn('âš ï¸ Error cleaning up YouTube player:', error);
                }
            }
            initializationRef.current = false;
        };
    }, [isDestroying]);
    if (isDestroying) {
        console.log('ðŸ’€ YouTubeTile is destroying, showing placeholder');
        return (_jsx("div", { className: "youtube-tile", style: {
                width: '310px',
                height: '180px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#000',
                color: '#fff',
                fontSize: '14px',
                borderRadius: '3px'
            }, children: "Removing video..." }));
    }
    return (_jsxs("div", { ref: containerRef, className: "youtube-tile", style: {
            position: 'relative',
            width: '310px',
            height: '180px',
            backgroundColor: '#000',
            borderRadius: '3px',
            overflow: 'hidden',
            cursor: (isHost && !isBroadcast) ? 'pointer' : 'default',
            border: '1px solid #8b5cf6',
        }, onClick: handleTileClick, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), children: [!isHost && (_jsx("div", { style: {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                    zIndex: 10,
                    pointerEvents: 'auto'
                } })), _jsx("div", { id: "youtube-player-singleton", style: {
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#000',
                    pointerEvents: 'none'
                } }), !playerReady && !isDestroying && (_jsx("div", { style: {
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#fff',
                    fontSize: '14px',
                    zIndex: 5
                }, children: "Loading YouTube player..." })), showUsername && !isBroadcast && (_jsx("div", { className: "video-username", style: {
                    position: 'absolute',
                    bottom: '5px',
                    left: '5px',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '12px',
                    zIndex: 5
                }, children: username })), needsUserInteraction && (_jsx("div", { style: {
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    color: 'white',
                    padding: '10px',
                    borderRadius: '5px',
                    textAlign: 'center',
                    fontSize: '14px',
                    zIndex: 10
                }, children: "Click to play video" })), _jsx("div", { style: {
                    opacity: volume === 0 ? 1 : (isHovered ? 1 : 0),
                    transition: 'opacity 0.2s ease',
                    pointerEvents: volume === 0 ? 'auto' : (isHovered ? 'auto' : 'none')
                }, children: _jsx(CompactVolumeSlider, { volume: volume, onVolumeChange: setGlobalVolume, isFeatured: isFeatured }) })] }));
};
export default YouTubeTile;
